import { ROUTES } from "../App";
import Lottie from "lottie-react";
import fireAnimation from "../assets/animations/fire.json";
import React, { useState } from "react";

const Explore = ({
    communities,
    users,
    sortType,
    currentUser,
    navigate,
    handleJoinCommunity,
    handleFollowUser,
}) => {
    const [loadedImages, setLoadedImages] = useState({});

    const handleImageLoad = (id) => {
        setLoadedImages((prev) => ({ ...prev, [id]: true }));
    };

    const renderCommunityCard = (community) => (
        <div
            key={community.id}
            className="bg-[#f4f4f4] dark:bg-[#242424] rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden cursor-pointer border border-gray-200 dark:border-gray-700 p-4 flex flex-row items-start gap-4"
            onClick={() => navigate(`${ROUTES.COMMUNITY_CHAT}/${community.id}`)}
        >
            {/* Profile Image */}
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0">
                {!loadedImages[community.id] && (
                    <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 rounded-full" />
                )}
                <img
                    src={
                        community.imageUrl ||
                        "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346"
                    }
                    alt={community.name}
                    className={`w-full h-full rounded-full object-cover transition-opacity duration-300 ${loadedImages[community.id] ? "opacity-100" : "opacity-0"}`}
                    onLoad={() => handleImageLoad(community.id)}
                    onError={() => handleImageLoad(community.id)}
                />
            </div>

            {/* Text Content */}
            <div className="flex-1 flex flex-col justify-between min-w-0">
                <div className="min-w-0">
                    {/* Name + Streak */}
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 dark:text-white truncate min-w-0">
                            {community.name}
                        </p>
                        {community.isActive && community.streakDays > 0 && (
                            <div className="flex items-center gap-1 bg-orange-100 dark:bg-orange-900/30 rounded-full px-2 py-1">
                                <Lottie animationData={fireAnimation} loop autoplay className="w-4 h-4" />
                                <span className="text-xs font-semibold text-orange-600 dark:text-orange-400">
                                    {community.streakDays} day streak
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {community.description || "All about design, inspiration and ideas..."}
                    </p>
                </div>

                {/* Join Button */}
                <div className="mt-3 self-end">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleJoinCommunity(community.id, community.isFollowed, community.memberCount);
                        }}
                        type="button"
                        className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors ${community.isFollowed ? "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600" : "bg-primary text-white"}`}
                    >
                        {community.isFollowed ? "Joined" : "Join"}
                    </button>
                </div>
            </div>
        </div>

    );


    const renderUserCard = (user, navigatePath) => (
        <div
            key={user.id}
            className="bg-[#f4f4f4] dark:bg-[#242424] rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden cursor-pointer border border-gray-200 dark:border-gray-700 p-4 flex flex-row items-start gap-4"
            onClick={() => navigate(navigatePath, {
                state: { from: "explore" }
            })}
        >
            {/* Profile Image */}
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0">
                {!loadedImages[user.id] && (
                    <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 rounded-full" />
                )}
                <img
                    src={
                        user.profilePic ||
                        "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef"
                    }
                    alt={user.name}
                    className={`w-full h-full rounded-full object-cover transition-opacity duration-300 ${loadedImages[user.id] ? "opacity-100" : "opacity-0"}`}
                    onLoad={() => handleImageLoad(user.id)}
                    onError={() => handleImageLoad(user.id)}
                />
            </div>

            {/* Text Content */}
            <div className="flex-1 flex flex-col justify-between min-w-0">
                <div className="min-w-0">
                    {/* Name + Optional Streak */}
                    <div className="flex-col gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 dark:text-white truncate min-w-0">
                            {user.name}
                        </p>
                        <p className="text-xs text-primary mt-1 line-clamp-2">
                            {user.niche || "No niche yet"}
                        </p>
                    </div>

                    {/* Bio */}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {user.about || "No bio yet"}
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="mt-3 self-end flex gap-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(navigatePath, { state: { from: "explore" } });
                        }}
                        className="px-2 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        View
                    </button>

                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleFollowUser(user);
                        }}
                        className={`px-2 py-1.5 text-xs font-medium rounded-lg transition-colors ${user.isFriend
                            ? "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                            : user.requestStatus === "sent"
                                ? "bg-gray-100 dark:bg-gray-700 text-gray-600"
                                : user.requestStatus === "received"
                                    ? "bg-green-500 text-white"
                                    : "bg-primary text-white"
                            }`}
                    >
                        {user.isFriend
                            ? "Friends"
                            : user.requestStatus === "sent"
                                ? "Requested"
                                : user.requestStatus === "received"
                                    ? "Accept"
                                    : "Friend"}
                    </button>

                </div>
            </div>
        </div>
    );


    const gridClasses = "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6";

    if (sortType === "communities") {
        return <div className={gridClasses}>{communities.map(renderCommunityCard)}</div>;
    }

    if (sortType === "people") {
        return <div className={gridClasses}>{users.filter((u) => u.id !== currentUser.uid).map((u) => renderUserCard(u, `${ROUTES.VIEW_PROFILE}/${u.id}`))}</div>;
    }

    if (sortType === "new") {
        const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

        const newUsers = users.filter((user) => {
            if (!user.createdAt) return false;

            const createdTime = new Date(user.createdAt).getTime();

            return createdTime >= oneWeekAgo && user.id !== currentUser.uid;
        });

        return newUsers.length === 0 ? (
            <p className="text-center text-gray-500 mt-10">
                No new users in the last week.
            </p>
        ) : (
            <div className={gridClasses}>
                {newUsers.map((user) =>
                    renderUserCard(user, `${ROUTES.VIEW_PROFILE}/${user.id}`)
                )}
            </div>
        );
    }
};

export default React.memo(Explore);